import React, { useContext, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiArrowRight, FiMinus, FiPlus, FiShoppingBag, FiTrash2 } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import "../css/cartpage.scss";

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity, clearCart } = useContext(CartContext);
  const [promoCode, setPromoCode] = useState("");
  const [promoMessage, setPromoMessage] = useState("");
  const [discountRate, setDiscountRate] = useState(0);
  const [giftNote, setGiftNote] = useState("");

  const detailedItems = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((product) => product.id === item.id),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = detailedItems.reduce(
    (total, item) => total + Number(item.product.price) * item.quantity,
    0
  );
  const discount = subtotal * discountRate;
  const discountedSubtotal = Math.max(0, subtotal - discount);
  const shipping = siteConfig.shipping?.standardRate ?? 9.99;
  const total = discountedSubtotal + shipping;

  const money = (value) =>
    new Intl.NumberFormat(siteConfig.currency?.locale || "en-US", {
      style: "currency",
      currency: siteConfig.currency?.code || "USD",
    }).format(Number(value));

  const applyPromo = (event) => {
    event.preventDefault();
    const demoCode = siteConfig.checkout?.demoPromoCode || "CLARITY10";
    const demoRate = siteConfig.checkout?.demoDiscountRate ?? 0.1;
    if (promoCode.trim().toUpperCase() === demoCode) {
      setDiscountRate(demoRate);
      setPromoMessage(`${demoCode} applied — ${demoRate * 100}% has been deducted.`);
    } else {
      setDiscountRate(0);
      setPromoMessage(`That code is not available. Try ${demoCode} for this demo.`);
    }
  };

  if (detailedItems.length === 0) {
    return (
      <main className="cart-empty" id="main-content">
        <div className="cart-empty__image">
          <img src="/img/generated/cart-empty.png" alt="A linen tote beside a neutral planner" />
        </div>
        <div className="cart-empty__copy">
          <FiShoppingBag />
          <p className="eyebrow">YOUR CART</p>
          <h1>Begin with one intentional page.</h1>
          <p>Your cart is empty. Explore planners and paper tools made to bring clarity to the day ahead.</p>
          <Link className="button button--dark" to="/shop">DISCOVER THE COLLECTION</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="cart-page" id="main-content">
      <header className="cart-page__header page-width">
        <div>
          <p className="eyebrow">YOUR SELECTION</p>
          <h1>SHOPPING CART</h1>
        </div>
        <Link className="text-link" to="/shop">CONTINUE SHOPPING</Link>
      </header>

      <section className="shipping-progress page-width">
        <p>Tracked standard shipping is a fixed <strong>{money(shipping)}</strong> for every supported destination.</p>
        <div><span style={{ width: "100%" }} /></div>
      </section>

      <div className="cart-page__layout page-width">
        <section className="cart-list" aria-label="Cart items">
          <div className="cart-list__labels"><span>PRODUCT</span><span>QUANTITY</span><span>TOTAL</span></div>
          {detailedItems.map((item) => {
            const lineTotal = Number(item.product.price) * item.quantity;
            return (
              <article className="cart-line" key={`${item.id}-${item.size}-${item.color}`}>
                <Link className="cart-line__image" to={`/shopDetails/${item.product.id}`}>
                  <img src={item.product.image} alt={item.product.name} />
                </Link>
                <div className="cart-line__details">
                  <p className="eyebrow">{item.product.category}</p>
                  <Link to={`/shopDetails/${item.product.id}`}>{item.product.name}</Link>
                  <span>{[item.size, item.color].filter(Boolean).join(" · ")}</span>
                  <span>{money(item.product.price)}</span>
                  <button type="button" onClick={() => removeFromCart(item.id, item.size, item.color)}>
                    <FiTrash2 /> REMOVE
                  </button>
                </div>
                <div className="cart-line__quantity">
                  <button type="button" onClick={() => updateQuantity(item.id, item.size, item.color, Math.max(1, item.quantity - 1))} aria-label="Decrease quantity"><FiMinus /></button>
                  <span>{item.quantity}</span>
                  <button type="button" onClick={() => updateQuantity(item.id, item.size, item.color, item.quantity + 1)} aria-label="Increase quantity"><FiPlus /></button>
                </div>
                <strong>{money(lineTotal)}</strong>
              </article>
            );
          })}

          <div className="cart-list__footer">
            <button type="button" onClick={clearCart}>CLEAR CART</button>
            <label htmlFor="gift-note">ORDER NOTE</label>
            <textarea id="gift-note" value={giftNote} onChange={(event) => setGiftNote(event.target.value)} placeholder="Add a gift note or delivery instruction (optional)" rows="3" />
          </div>
        </section>

        <aside className="cart-summary">
          <p className="eyebrow">ORDER SUMMARY</p>
          <form className="promo-form" onSubmit={applyPromo}>
            <label htmlFor="promo-code">PROMO CODE</label>
            <div>
              <input id="promo-code" value={promoCode} onChange={(event) => setPromoCode(event.target.value)} placeholder="Enter code" />
              <button type="submit">APPLY</button>
            </div>
            <p aria-live="polite">{promoMessage}</p>
          </form>
          <dl>
            <div><dt>Subtotal</dt><dd>{money(subtotal)}</dd></div>
            {discount > 0 && <div className="cart-summary__discount"><dt>Discount</dt><dd>−{money(discount)}</dd></div>}
            <div><dt>Shipping</dt><dd>{money(shipping)}</dd></div>
          </dl>
          <div className="cart-summary__total"><span>TOTAL</span><strong>{money(total)}</strong></div>
          <p>The full merchandise and tracked delivery amount is shown before payment.</p>
          <button
            type="button"
            className="cart-checkout"
            onClick={() => {
              navigate("/checkout", {
                state: {
                  discountRate,
                  promoCode: discountRate > 0 ? promoCode.trim().toUpperCase() : "",
                  giftNote: giftNote.trim(),
                },
              });
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            SECURE CHECKOUT <FiArrowRight />
          </button>
          <div className="cart-summary__assurance">
            <span>SECURE PAYMENT</span><span>{siteConfig.shipping?.returnsWindowDays ?? 30}-DAY RETURNS</span><span>CONCIERGE CARE</span>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default CartPage;
